import React, { useEffect, useMemo, useRef } from 'react'
import Select from '../Select/Select'
import { addDays, addMonths, DateRange, startOfDay } from './dateRange'

interface Props {
    label: string
    month: Date
    selected: Date | null
    range: DateRange | null
    onMonthChange: (month: Date) => void
    onSelect: (date: Date) => void
    minDate?: Date
    maxDate?: Date
    locale: string
    classes: Record<string, string>
}

/**
 * A month calendar with inclusive range highlighting and keyboard navigation.
 */
const RangeCalendar = ({
    label, month, selected, range, onMonthChange, onSelect, minDate, maxDate, locale, classes
}: Props): JSX.Element => {
    const gridRef = useRef<HTMLDivElement>(null)
    const pendingFocus = useRef<number | null>(null)
    const year = month.getFullYear()
    const monthIndex = month.getMonth()
    const firstDay = new Date(year, monthIndex, 1).getDay()
    const dayCount = new Date(year, monthIndex + 1, 0).getDate()
    const minimum = minDate && startOfDay(minDate)
    const maximum = maxDate && startOfDay(maxDate)
    const isDisabled = (date: Date) => !!((minimum && date < minimum) || (maximum && date > maximum))
    const currentYear = new Date().getFullYear()
    const yearsFrom = minDate ? Math.min(minDate.getFullYear(), year) : Math.min(currentYear - 100, year)
    const yearsTo = Math.max(currentYear + 100, year)
    const monthOptions = useMemo(() => Array.from({ length: 12 }, (unused, index) => ({
        value: index + 1,
        text: new Date(2020, index, 1).toLocaleDateString(locale, { month: 'long' })
    })), [locale])
    const minYear = minDate?.getFullYear()
    const maxYear = maxDate?.getFullYear()
    const yearOptions = useMemo(() => Array.from({ length: yearsTo - yearsFrom + 1 }, (unused, index) => {
        const value = yearsTo - index
        return {
            value,
            text: String(value),
            disabled: (minYear !== undefined && value < minYear) || (maxYear !== undefined && value > maxYear)
        }
    }), [yearsFrom, yearsTo, minYear, maxYear])
    const dateLabel = (date: Date) => date.toLocaleDateString(locale, {
        day: 'numeric', month: 'long', year: 'numeric'
    })

    useEffect(() => {
        if (pendingFocus.current !== null) {
            const button = gridRef.current?.querySelector<HTMLButtonElement>(
                `[data-day="${pendingFocus.current}"]`
            )
            button?.focus()
            pendingFocus.current = null
        }
    })

    const moveFocus = (event: React.KeyboardEvent<HTMLButtonElement>, date: Date) => {
        let next: Date
        switch (event.key) {
                case 'ArrowLeft':
                    next = addDays(date, -1)
                    break
                case 'ArrowRight':
                    next = addDays(date, 1)
                    break
                case 'ArrowUp':
                    next = addDays(date, -7)
                    break
                case 'ArrowDown':
                    next = addDays(date, 7)
                    break
                case 'Home':
                    next = addDays(date, -date.getDay())
                    break
                case 'End':
                    next = addDays(date, 6 - date.getDay())
                    break
                case 'PageUp':
                    next = addMonths(date, -1)
                    break
                case 'PageDown':
                    next = addMonths(date, 1)
                    break
                default:
                    return
        }
        event.preventDefault()
        if (minimum && next < minimum) next = minimum
        if (maximum && next > maximum) next = maximum
        pendingFocus.current = next.getDate()
        onMonthChange(new Date(next.getFullYear(), next.getMonth(), 1))
    }

    return <section className={classes.calendar} aria-label={`${label} calendar`}>
        <h3 className={classes.heading}>{label}</h3>
        <div className={classes.selectors}>
            <Select
                title={`${label} month`}
                value={monthIndex + 1}
                required
                fullWidth
                data-testid={`calendar-${label.toLowerCase()}-month`}
                options={monthOptions}
                onChange={value => onMonthChange(new Date(year, Number(value) - 1, 1))}
            />
            <Select
                title={`${label} year`}
                value={year}
                required
                fullWidth
                data-testid={`calendar-${label.toLowerCase()}-year`}
                options={yearOptions}
                onChange={value => onMonthChange(new Date(Number(value), monthIndex, 1))}
            />
        </div>
        <div className={classes.grid} ref={gridRef}>
            {Array.from({ length: 7 }, (unused, index) => {
                const date = new Date(2023, 0, 1 + index)
                return <span
                    key={`weekday-${index}`}
                    className={classes.weekday}
                    title={date.toLocaleDateString(locale, { weekday: 'long' })}
                >{date.toLocaleDateString(locale, { weekday: 'narrow' })}</span>
            })}
            {Array.from({ length: firstDay }, (unused, index) => <span key={`empty-${index}`} />)}
            {Array.from({ length: dayCount }, (unused, index) => {
                const date = new Date(year, monthIndex, index + 1)
                const endpoint = !!((range?.from && +date === +range.from) || (range?.to && +date === +range.to))
                return <button
                    type='button'
                    key={index + 1}
                    data-day={index + 1}
                    className={classes.day}
                    aria-label={`${label} ${dateLabel(date)}`}
                    aria-pressed={endpoint}
                    aria-current={+date === +startOfDay(new Date()) ? 'date' : undefined}
                    data-in-range={!!(range?.from || range?.to) &&
                        (!range?.from || date >= range.from) && (!range?.to || date <= range.to)}
                    disabled={isDisabled(date)}
                    tabIndex={selected?.getFullYear() === year && selected?.getMonth() === monthIndex
                        ? (+date === +selected ? 0 : -1)
                        : (index === 0 || (minimum && +date === +minimum) ? 0 : -1)}
                    onClick={() => onSelect(date)}
                    onKeyDown={event => moveFocus(event, date)}
                >{index + 1}</button>
            })}
        </div>
    </section>
}

export default RangeCalendar
